import { Skeleton } from '@branchjs/neo-components';
import classNames from 'classnames';
import * as R from 'ramda';
import type { ReactElement } from 'react';
import React, { PureComponent } from 'react';

import Input from './Input';

// TODO: SearchBar does not work with "(1)", which is what we append to
// cloned Journey titles.

function filterListBySearchTerm(searchTerm, rows: any[] = [], excludedKeys, includedKeys) {
  if (!searchTerm) {
    return rows;
  }

  // add a backslash in front of any special character to escape it (ex: adams+testorg1@branch.io -> adams\+testorg1@branch\.io)
  searchTerm = searchTerm.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');

  const expression = new RegExp(searchTerm, 'gi');

  return R.filter((row) => {
    if (row.constructor !== Object) {
      return !!JSON.stringify(row).match(expression);
    }

    const prunedObj = includedKeys.length ? R.values(R.pick(includedKeys, row)) : R.values(R.omit(excludedKeys, row));
    return !!JSON.stringify(prunedObj).match(expression);
  }, rows);
}

const filterListBySearchTermMemoized = R.memoize(filterListBySearchTerm);

type Props = {
  /**
   * Wrapped element - rendered below the search bar.
   */
  children: ReactElement;
  /**
   * class name applied to input itself. Either string or an object of class names (i.e. `{ 'foo-class': true, 'bar-class': false }`)
   */
  className?: string;
  /**
   * list of data, that will be filtered based on search text
   */
  data: unknown[];
  /**
   * preset text search input and filtering
   */
  initText?: string;
  /**
   * don't memoize searches
   */
  noCache?: boolean;
  /**
   * class name applied to outer container.
   */
  outerClassName?: string;
  /**
   * standard testid prop (converted to `data-testid`) passed into the input component
   */
  testid?: string;
  /**
   * if passed, search will only match values of these keys. Takes precedence over excludedKeys
   */
  includedKeys?: unknown[];
  /**
   * if included keys is not passed, excludedKeys will omit keys from search
   */
  excludedKeys?: unknown[];
  /**
   *  If set to true, loading placeholder is displayed.
   */
  loading?: boolean;
  /**
   * Callback fired when the user changes input in the search bar.
   */
  onChange?: (value: string) => void;
};

type State = {
  text: string;
};

class SearchBar extends PureComponent<Props, State> {
  static defaultProps = {
    data: [],
    initText: '',
    noCache: false,
    testid: 'search-component',
    includedKeys: [],
    excludedKeys: [],
  };

  constructor(props) {
    super(props);

    this.state = { text: props.initText };
  }

  _onChange = (e) => {
    this.setState({ text: e.value });

    if (this.props.onChange) {
      this.props.onChange(e.target.value);
    }
  };

  render() {
    const { children, data, className, outerClassName, loading, noCache, excludedKeys, includedKeys, testid } = this.props;
    const { text } = this.state;

    const filterFunction = noCache ? filterListBySearchTerm : filterListBySearchTermMemoized;

    return (
      <div className="search-bar-wrapper">
        <div className={classNames('h_pos-r', outerClassName)}>
          {loading ? (
            <Skeleton className="default-skeleton" variant="rectangular" height={36} />
          ) : (
            <>
              <Input
                className={classNames('search-bar-input', className)}
                placeholder="Search"
                value={text}
                testid={testid || 'search-bar-input'}
                onChange={this._onChange}
              />
              <i className="material-icons search-bar-icon">search</i>
            </>
          )}
        </div>

        {React.cloneElement(children, {
          data: filterFunction(text, data, excludedKeys, includedKeys),
        })}
      </div>
    );
  }
}

export default SearchBar;
